import {
  Chart,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
} from "chart.js";
import { SankeyController, Flow } from "chartjs-chart-sankey";
import { getRootNodes } from "./energyReportData";

Chart.register(
  SankeyController,
  Flow,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
);

const COLOR_PALETTE = [
  "#009f8d",
  "#ff9c07",
  "#1e88e5",
  "#8e24aa",
  "#0ea5e9",
  "#facc15",
  "#00c6a9",
  "#ffb74d",
];

const getNodeColor = (depth) =>
  COLOR_PALETTE[(depth - 1) % COLOR_PALETTE.length];

function parseEnergy(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value.replace(/[^\d.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function formatKwhLabel(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function buildSankeyChartConfig(hierarchyData, scopeNodeName, maxLevels = 4) {
  if (!hierarchyData || typeof hierarchyData !== "object") {
    return { chartData: { datasets: [{ data: [] }] }, nodeStyles: {}, flowRows: [] };
  }

  const isWholeFacility = !scopeNodeName || scopeNodeName === "Usage";
  const rootNodes = isWholeFacility
    ? getRootNodes(hierarchyData)
    : hierarchyData[scopeNodeName]
      ? [hierarchyData[scopeNodeName]]
      : [];

  const dataPoints = [];
  const nodeStyles = {};
  const processedNodes = new Set();

  const traverseNode = (node, depth = 1) => {
    if (!node || depth > maxLevels) return;

    const nodeId = node.machine_name;
    if (!nodeId || processedNodes.has(nodeId)) return;
    processedNodes.add(nodeId);

    const consumed = parseEnergy(node.energy_consumed);
    if (consumed <= 0) return;

    nodeStyles[nodeId] = {
      label: `${node.machine_name} (${formatKwhLabel(consumed)} kWh)`,
      color: node.color || getNodeColor(depth),
    };

    (node.children || []).forEach((child) => {
      const childId = child.machine_name;
      const childConsumed = parseEnergy(child.energy_consumed);
      if (!childId || childConsumed <= 0) return;

      dataPoints.push({ from: nodeId, to: childId, flow: childConsumed });
      traverseNode(child, depth + 1);
    });
  };

  rootNodes.forEach((root) => traverseNode(root));

  return {
    flowRows: dataPoints,
    nodeStyles,
    chartData: {
      datasets: [
        {
          label: "Energy Flow",
          data: dataPoints,
          colorFrom: (ctx) => nodeStyles[ctx?.raw?.from]?.color || "#94a3b8",
          colorTo: (ctx) => nodeStyles[ctx?.raw?.from]?.color || "#94a3b8",
          colorMode: "from",
          alpha: 0.65,
          size: "max",
          borderWidth: 0,
          labels: {
            formatter: (value) => nodeStyles[value]?.label || value,
            font: { size: 11, weight: "bold" },
            color: "#334155",
          },
          node: { width: 28, padding: 24 },
        },
      ],
    },
  };
}

export function renderSankeyChartImage(hierarchyData, scopeNodeName, maxLevels = 4) {
  const { chartData, flowRows } = buildSankeyChartConfig(
    hierarchyData,
    scopeNodeName,
    maxLevels,
  );

  if (!flowRows.length) {
    const canvas = document.createElement("canvas");
    canvas.width = 900;
    canvas.height = 320;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#64748b";
    ctx.font = "16px Helvetica, Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Not enough hierarchy data to build Sankey diagram.",
      canvas.width / 2,
      canvas.height / 2,
    );
    return canvas.toDataURL("image/png");
  }

  const canvas = document.createElement("canvas");
  canvas.width = 1000;
  canvas.height = 520;

  const chart = new Chart(canvas, {
    type: "sankey",
    data: chartData,
    options: {
      responsive: false,
      animation: false,
      plugins: {
        legend: { display: false },
      },
    },
  });

  chart.draw();
  const image = canvas.toDataURL("image/png");
  chart.destroy();
  return image;
}
